import React, { useEffect, useState } from "react";
import { SweetAlertShow, LoadingDiv } from "../master/Master";
import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import { batchDetailsWithStudents, batchList } from "../../api/Batch";
import StudentStudyCertificate1 from "../components/StudentStudyCertificate1";
import { printElement, printImg } from "../master/Print";
import IdCardLayout from "./IdCardLayout";
import usePrivateLink from "../config/usePrivateLink";

const errMsg = (error) =>
  error?.response?.data?.message ?? error?.response?.message ?? error?.message;

export default function StudentStudyCertificate() {
  const [loading, setLoading] = useState(false);
  const hasAccess = usePrivateLink();

  const [batches, setBatches] = useState([]);
  const [batchData, setBatchData] = useState({});
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState("");
  const [certificateNo, setCertificateNo] = useState("");
  const [issueDate, setIssueDate] = useState(
    new Date().toISOString().slice(0, 10),
  );

  const getBatchList = async () => {
    setLoading(true);
    try {
      const response = await batchList();
      if (response.data.status) {
        setBatches(response?.data?.data);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow("error", "Error", errMsg(error));
    } finally {
      setLoading(false);
    }
  };

  const getBatchInfo = async (id) => {
    setLoading(true);
    try {
      const response = await batchDetailsWithStudents(`&id=${id}&status=1`);
      if (response.data.status) {
        setBatchData(response?.data?.data);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow("error", "Error", errMsg(error));
    } finally {
      setLoading(false);
    }
  };

  const handleStudentChange = (value) => {
    setSelectedStudent(value);
    const all = batchData?.active_batch_students || [];
    if (value === "all") {
      setStudents(all);
    } else {
      setStudents(all.filter((s) => s?.student_id === Number(value)));
    }
  };

  const handlePrint = () => {
    if (!students.length) {
      SweetAlertShow("info", "Warning", "Please select a student first");
      return;
    }
    printImg("StudyPrintDiv");
  };

  useEffect(() => {
    getBatchList();
  }, []);

  return (
    <>
      {loading && <LoadingDiv />}
      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />
          <div className="layout-page">
            <Nav />
            <div className="content-wrapper">
              <div className="container-fluid flex-grow-1 container-p-y">
                <div className="row">
                  <IdCardLayout />
                  <div className="col-12">
                    <div className="card">
                      <div className="card-body">
                        <div className="row g-3">
                          <div className="col-12">
                            <h4 className="text-primary m-0">
                              Student Study Certificate
                            </h4>
                          </div>
                          <div className="col-12">
                            <hr className="my-0" />
                          </div>

                          <div className="col-md-3">
                            <div className="form-floating form-floating-outline">
                              <select
                                className="form-select"
                                id="batch_id"
                                name="batch_id"
                                defaultValue=""
                                onChange={(e) => {
                                  setBatchData({});
                                  setStudents([]);
                                  setSelectedStudent("");
                                  if (e.target.value)
                                    getBatchInfo(e.target.value);
                                }}
                              >
                                <option value="" disabled>
                                  Select Batch
                                </option>
                                {batches?.map((batch, i) => (
                                  <option key={i} value={batch?.id}>
                                    {batch?.title} ({batch?.course?.title})
                                  </option>
                                ))}
                              </select>
                              <label htmlFor="batch_id">Select Batch</label>
                            </div>
                          </div>

                          {batchData?.active_batch_students && (
                            <div className="col-md-3">
                              <div className="form-floating form-floating-outline">
                                <select
                                  className="form-select"
                                  id="student_id"
                                  name="student_id"
                                  value={selectedStudent}
                                  onChange={(e) =>
                                    handleStudentChange(e.target.value)
                                  }
                                >
                                  <option value="" disabled>
                                    Select Student
                                  </option>
                                  {batchData.active_batch_students.length >
                                    1 && (
                                    <option value="all">All Students</option>
                                  )}
                                  {batchData.active_batch_students.map(
                                    (student, i) => (
                                      <option
                                        key={i}
                                        value={student?.student_id}
                                      >
                                        {student?.student_info?.name} (
                                        {student?.student_info?.registration_no}
                                        )
                                      </option>
                                    ),
                                  )}
                                </select>
                                <label htmlFor="student_id">
                                  Select Student
                                </label>
                              </div>
                            </div>
                          )}

                          <div className="col-md-2">
                            <div className="form-floating form-floating-outline">
                              <input
                                type="date"
                                className="form-control"
                                id="issue_date"
                                value={issueDate}
                                onChange={(e) => setIssueDate(e.target.value)}
                              />
                              <label htmlFor="issue_date">Date</label>
                            </div>
                          </div>

                          <div className="col-md-2">
                            <div className="form-floating form-floating-outline">
                              <input
                                type="text"
                                className="form-control"
                                id="certificate_no"
                                placeholder="Certificate No"
                                value={certificateNo}
                                onChange={(e) =>
                                  setCertificateNo(e.target.value)
                                }
                              />
                              <label htmlFor="certificate_no">
                                Certificate No (optional)
                              </label>
                            </div>
                          </div>

                          <div className="col-md-2 d-flex align-items-center">
                            {hasAccess("/card/certificate/print") && (
                              <button
                                className="btn btn-primary"
                                onClick={handlePrint}
                                disabled={!students.length}
                              >
                                Print
                              </button>
                            )}
                          </div>

                          <div className="col-12">
                            <hr className="my-0" />
                          </div>

                          <div
                            className="col-12 d-flex flex-column align-items-center gap-4"
                            id="StudyPrintDiv"
                          >
                            {students?.map((student, i) => (
                              <StudentStudyCertificate1
                                key={student?.id ?? i}
                                data={student}
                                batch={batchData}
                                issueDate={issueDate}
                                certificateNo={certificateNo}
                              />
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
